<template>
    <section class="uncertainty-panel">
        <header class="panel-header">
            <div>
                <h4 class="panel-title">불확도 평가</h4>
                <p class="panel-subtitle">IPCC 2006 가이드라인 RSS 방법 기반 자동 평가 결과입니다.</p>
            </div>
        </header>

        <div v-if="loading" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <span>불확도을 계산하는 중입니다...</span>
        </div>
        <div v-else-if="!uncertainty" class="empty-state">
            <i class="fas fa-balance-scale"></i>
            <p>아직 불확도 평가가 실행되지 않았습니다.</p>
        </div>
        <div v-else class="content-uncertainty">
            <section class="summary">
                <div class="summary-card">
                    <span class="summary-label">총 불확도</span>
                    <strong class="summary-value"
                        >{{ uncertainty.uncertainty?.percentage?.toFixed(2) || "0.00" }}%</strong
                    >
                    <span class="summary-level" :class="`level-${uncertainty.uncertainty?.level}`">{{
                        levelLabel
                    }}</span>
                </div>
                <div class="summary-card">
                    <span class="summary-label">총 배출량</span>
                    <strong class="summary-value">{{ formatEmission(uncertainty.totalEmissions) }}</strong>
                    <span class="summary-unit">kgCO2e</span>
                </div>
                <div class="summary-card">
                    <span class="summary-label">95% 신뢰구간</span>
                    <strong class="summary-value"
                        >{{ formatEmission(interval.lower) }} ~ {{ formatEmission(interval.upper) }}</strong
                    >
                    <span class="summary-unit">kgCO2e ({{ interval.confidence }}%)</span>
                </div>
            </section>

            <section class="breakdown">
                <h5>요소별 불확도</h5>
                <div class="breakdown-grid">
                    <article class="breakdown-card">
                        <header>
                            <span>활동 데이터</span>
                            <small>Tier {{ breakdown.activityData?.tier || "-" }}</small>
                        </header>
                        <strong>{{ breakdown.activityData?.uncertainty || "-" }}%</strong>
                        <p>{{ breakdown.activityData?.source || "" }}</p>
                    </article>
                    <article class="breakdown-card">
                        <header>
                            <span>배출계수</span>
                            <small>Tier {{ breakdown.emissionFactor?.tier || "-" }}</small>
                        </header>
                        <strong>{{ breakdown.emissionFactor?.uncertainty || "-" }}%</strong>
                        <p>{{ breakdown.emissionFactor?.source || "" }}</p>
                    </article>
                    <article class="breakdown-card">
                        <header>
                            <span>결합</span>
                            <small>{{ uncertainty.uncertainty?.method?.standard }}</small>
                        </header>
                        <strong>{{ breakdown.combined || "-" }}%</strong>
                        <p>{{ uncertainty.uncertainty?.method?.remarks }}</p>
                    </article>
                </div>
            </section>

            <section class="scope-table">
                <h5>Scope별 불확도</h5>
                <table class="table table-bordered table-sm">
                    <thead class="thead-light">
                        <tr>
                            <th>Scope</th>
                            <th>배출량 (kgCO2e)</th>
                            <th>불확도 (%)</th>
                            <th>수준</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in uncertainty.byScope || []" :key="item.scope">
                            <td>Scope {{ item.scope }}</td>
                            <td>{{ formatEmission(item.emissions) }}</td>
                            <td>{{ item.uncertainty?.toFixed?.(2) || item.uncertainty }}</td>
                            <td>
                                <span class="badge" :class="`badge-${item.level}`">{{
                                    scopeLevelLabel(item.level)
                                }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>
        </div>
    </section>
</template>

<script>
export default {
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        uncertainty: {
            type: Object,
            default: null,
        },
    },
    computed: {
        levelLabel() {
            const level = this.uncertainty?.uncertainty?.level;
            switch (level) {
                case "low":
                    return "Low";
                case "medium":
                    return "Medium";
                case "high":
                    return "High";
                default:
                    return "Unknown";
            }
        },
        interval() {
            return this.uncertainty?.uncertainty?.confidenceInterval || { lower: 0, upper: 0, confidence: 95 };
        },
        breakdown() {
            return this.uncertainty?.uncertainty?.breakdown || {};
        },
    },
    methods: {
        formatEmission(value) {
            if (value == null) return "-";
            return Number(value).toLocaleString("ko-KR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        scopeLevelLabel(level) {
            switch (level) {
                case "low":
                    return "낮음";
                case "medium":
                    return "중간";
                case "high":
                    return "높음";
                default:
                    return level || "-";
            }
        },
    },
};
</script>

<style scoped>
.uncertainty-panel {
    margin-top: 24px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px 24px;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

:deep(.uncertainty-refresh.btn-success) {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    border: none;
    color: #ffffff;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.25);
}

:deep(.uncertainty-refresh.btn-success:hover),
:deep(.uncertainty-refresh.btn-success:focus) {
    background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
    color: #ffffff;
}

:deep(.uncertainty-refresh.btn-success:disabled) {
    background: linear-gradient(135deg, #bbf7d0 0%, #86efac 100%);
    box-shadow: none;
}

.panel-title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f4a35;
}

.panel-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #64748b;
}

.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 200px;
    color: #64748b;
}

.content-uncertainty {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-top: 12px;
}

.summary-card {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 12px;
    padding: 16px;
}

.summary-label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #0f766e;
}

.summary-value {
    display: block;
    margin: 8px 0;
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
}

.summary-unit {
    font-size: 12px;
    color: #475569;
}

.summary-level {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
}

.summary-level.level-low {
    color: #16a34a;
}

.summary-level.level-medium {
    color: #f59e0b;
}

.summary-level.level-high {
    color: #dc2626;
}

.breakdown h5,
.scope-table h5 {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 12px;
}

.breakdown-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.breakdown-card {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
    background: #f8fafc;
}

.breakdown-card header {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 8px;
}

.breakdown-card strong {
    font-size: 20px;
    font-weight: 800;
    color: #0f4a35;
}

.breakdown-card p {
    margin: 6px 0 0;
    font-size: 12px;
    color: #475569;
}

.badge-low {
    background: #dcfce7;
    color: #166534;
}

.badge-medium {
    background: #fef3c7;
    color: #92400e;
}

.badge-high {
    background: #fee2e2;
    color: #b91c1c;
}

@media (max-width: 768px) {
    .panel-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
}
</style>
